<template>
  <div
    v-if="isEnabled && hasContent"
    ref="bar"
    class="m-announcement-bar"
  >
    <div class="m-announcement-bar__content">
      <div ref="content" class="m-announcement-bar__html" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'MAnnouncementBar',
  data () {
    return {
      resizeObserver: null,
      countdownIntervals: []
    }
  },
  computed: {
    isEnabled () {
      return this.storeConfig('ruckzuck_theme/announcement_bar/enabled') === '1'
    },
    htmlContent () {
      return this.storeConfig('ruckzuck_theme/announcement_bar/html_content') || ''
    },
    hasContent () {
      return this.htmlContent.trim().length > 0
    }
  },
  mounted () {
    this.renderContent()
    this.updateBarHeight()

    if (typeof ResizeObserver !== 'undefined' && this.$refs.bar) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateBarHeight()
      })
      this.resizeObserver.observe(this.$refs.bar)
    } else {
      window.addEventListener('resize', this.updateBarHeight)
    }
  },
  beforeDestroy () {
    this.clearCountdowns()

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    } else {
      window.removeEventListener('resize', this.updateBarHeight)
    }

    this.setBarHeightCssVar(0)
  },
  methods: {
    storeConfig (path) {
      return this.$store.getters['storeConfig/getConfigValue'](path)
    },
    renderContent () {
      this.$nextTick(() => {
        const container = this.$refs.content
        if (!container) return

        this.clearCountdowns()
        container.innerHTML = this.htmlContent
        this.initCountdowns(container)
        this.updateBarHeight()
      })
    },
    clearCountdowns () {
      this.countdownIntervals.forEach((intervalId) => {
        window.clearInterval(intervalId)
      })
      this.countdownIntervals = []
    },
    initCountdowns (container) {
      const elements = container.querySelectorAll('[data-countdown]')

      elements.forEach((element) => {
        const target = this.parseCountdownDate(element.getAttribute('data-countdown'))
        if (!target) return

        const expiredText = element.getAttribute('data-countdown-expired') || ''

        const render = () => {
          const diff = target - Date.now()

          if (diff <= 0) {
            element.textContent = expiredText
            return true
          }

          element.textContent = this.formatCountdown(diff)
          return false
        }

        if (render()) return

        const intervalId = window.setInterval(() => {
          if (render()) {
            window.clearInterval(intervalId)
            this.countdownIntervals = this.countdownIntervals.filter((id) => id !== intervalId)
          }
        }, 1000)

        this.countdownIntervals.push(intervalId)
      })
    },
    parseCountdownDate (dateStr) {
      if (!dateStr) return null

      const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/)
      if (!match) return null

      const [, year, month, day, hours, minutes, seconds] = match

      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds)
      ).getTime()
    },
    formatCountdown (diffMs) {
      const totalSeconds = Math.floor(diffMs / 1000)
      const days = Math.floor(totalSeconds / 86400)
      const hours = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      return `${days}T ${hours}h ${minutes}m ${seconds}s`
    },
    updateBarHeight () {
      this.$nextTick(() => {
        const bar = this.$refs.bar
        const height = bar ? Math.ceil(bar.getBoundingClientRect().height) : 0

        this.setBarHeightCssVar(height)
      })
    },
    setBarHeightCssVar (height) {
      if (typeof document === 'undefined') return

      document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`)
    }
  },
  watch: {
    htmlContent () {
      this.renderContent()
    },
    isEnabled () {
      this.$nextTick(() => {
        this.renderContent()
        this.updateBarHeight()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/helpers/breakpoints";

.m-announcement-bar {
  position: sticky;
  top: 0;
  // Above page content / sticky buy-box offset, but below menus, sidebars and modals
  z-index: 2;
  width: 100%;
  color: #ffffff;
  background: #E42313;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

  &__content {
    max-width: 1440px;
    margin: 0 auto;
    padding: 0.4rem 1rem;

    @include for-desktop {
      padding: 0.45rem 1.5rem;
    }
  }

  &__html {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.25;
    font-size: 0.875rem;

    ::v-deep a {
      color: inherit;
      text-decoration: underline;
    }

    ::v-deep p,
    ::v-deep div,
    ::v-deep span,
    ::v-deep strong {
      color: inherit;
    }

    ::v-deep p {
      margin: 0;
    }
  }
}
</style>
